/**
 * Pane de detalhe — formato "ficha" enterprise.
 * Chip colorido + título serif + blockquote + cards de metadados + botões reais.
 *
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import {
  ArrowLeft, Clock, ExternalLink, Archive, MailOpen, Mail,
  Link2, BellOff, MessageSquare,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  PROVIDER_LOGOS,
  formatEditorialDateLong,
  getModuleKicker,
  getModulePalette,
  getNotificationIcon,
} from './notification-utils';
import { cn } from '@/lib/utils';
import type { NotificationCenterItem } from '@/hooks/useNotificationCenter';

interface Props {
  notification: NotificationCenterItem | null;
  onBack?: () => void;
  onOpenEntity: (n: NotificationCenterItem) => void;
  onMarkRead: (n: NotificationCenterItem) => void;
  /** v3.1.0: reverte item lido para NÃO lido (uso como lembrete). */
  onMarkUnread: (n: NotificationCenterItem) => void;
  onArchive: (n: NotificationCenterItem) => void;
  showBackButton?: boolean;
}

export function NotificationDetailPane({
  notification, onBack, onOpenEntity, onMarkRead, onMarkUnread, onArchive, showBackButton,
}: Props) {

  if (!notification) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 px-8 text-muted-foreground text-center bg-background">
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-5">
          <MailOpen className="h-6 w-6 text-muted-foreground shrink-0" strokeWidth={1.5} />
        </div>
        <p className="text-[20px] font-medium tracking-tight text-foreground/90 mb-1">
          Nenhuma notificação selecionada
        </p>
        <p className="text-[12.5px] text-muted-foreground max-w-[280px]">
          Escolha um item da lista para ler os detalhes e tomar uma ação.
        </p>
      </div>
    );
  }

  const n = notification;
  const meta = n.metadata || {};
  const palette = getModulePalette(n.type);
  const providerLogo = n.type === 'contract_signed' && meta.provider_slug
    ? PROVIDER_LOGOS[meta.provider_slug]
    : undefined;
  const kicker = getModuleKicker(n.type);

  const fields: Array<{ label: string; value?: string | number | null }> = [
    { label: 'Contato',       value: meta.contact_name },
    { label: 'Telefone',      value: meta.contact_phone },
    { label: 'Departamento',  value: meta.department_name },
    { label: 'Atividade',     value: meta.activity_title },
    { label: 'Negócio',       value: meta.deal_title },
    { label: 'Contrato',      value: meta.contract_title },
    { label: 'Signatário',    value: meta.signer_name },
    { label: 'Cliente',       value: meta.customer_name },
    { label: 'Valor pago',    value: meta.valor_pago != null ? `R$ ${Number(meta.valor_pago).toFixed(2)}` : undefined },
    { label: 'Tarefa',        value: meta.task_title },
    { label: 'Categoria',     value: meta.category_name },
    { label: 'Prioridade',    value: meta.priority },
    { label: 'Atribuído por', value: meta.assigner_name },
    { label: 'Autor',         value: meta.author_name },
  ].filter(f => f.value != null && f.value !== '');

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header com botão voltar (mobile) */}
      {showBackButton && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card">
          <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label="Voltar">
            <ArrowLeft strokeWidth={1.75} />
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1" preventScrollChaining>
        <article className="px-8 py-8 max-w-[680px] mx-auto">
          {/* Kicker — chip colorido do módulo */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 h-6 px-2 rounded-full',
                'text-[10.5px] font-semibold uppercase tracking-[0.12em]',
                palette.chip,
              )}
            >
              <span className={palette.icon}>
                {getNotificationIcon(n.type)}
              </span>
              {kicker}
            </span>
            {providerLogo && (
              <img
                src={providerLogo}
                alt=""
                className="h-4 w-4 object-contain opacity-80"
              />
            )}
            {n.archived_at && (
              <span className="inline-flex items-center h-6 px-2 rounded-full bg-muted text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                arquivada
              </span>
            )}
          </div>

          {/* Título — sans-serif consistente com o header "Notificações" */}
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.25] text-foreground">
            {n.title}
          </h1>

          {/* Timestamp com ícone */}
          <div className="flex items-center gap-1.5 mt-2.5 text-[12.5px] text-muted-foreground">
            <Clock className="h-3 w-3" strokeWidth={1.75} />
            <span className="tabular-nums">{formatEditorialDateLong(n.created_at)}</span>
          </div>

          {/* Mensagem como blockquote colorido */}
          {n.message && (
            <div
              className={cn(
                'mt-6 p-4 rounded-r-md bg-muted/40 border-l-2',
                palette.border,
              )}
            >
              <p className="text-[13.5px] leading-relaxed text-foreground/90 whitespace-pre-wrap">
                {n.message}
              </p>
            </div>
          )}

          {/* Cards de metadados */}
          {fields.length > 0 && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {fields.map(f => (
                <div
                  key={f.label}
                  className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5"
                >
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1">
                    {f.label}
                  </dt>
                  <dd className="text-[13px] font-medium text-foreground break-words">
                    {String(f.value)}
                  </dd>
                </div>
              ))}
            </div>
          )}

          {/* Ações primárias — tamanho xs igual ao botão "Nova Tarefa" da listagem */}
          <div className="mt-7 flex flex-wrap items-center gap-2">
            <Button
              size="xs"
              onClick={() => onOpenEntity(n)}
              className="gap-1.5"
            >
              <ExternalLink strokeWidth={2} />
              Abrir {kicker.toLowerCase()}
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => onArchive(n)}
              className="gap-1.5"
            >
              <Archive strokeWidth={1.75} />
              {n.archived_at ? 'Desarquivar' : 'Arquivar'}
            </Button>
          </div>

          {/* Ações utilitárias secundárias — size xs para hierarquia */}
          <div className="mt-4 flex items-center gap-1 pt-4 border-t border-border/60">
            {n.is_read ? (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onMarkUnread(n)}
                className="text-muted-foreground hover:text-foreground gap-1.5"
              >
                <Mail className="h-3.5 w-3.5" strokeWidth={1.75} />
                Marcar como não lida
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onMarkRead(n)}
                className="text-muted-foreground hover:text-foreground gap-1.5"
              >
                <MailOpen className="h-3.5 w-3.5" strokeWidth={1.75} />
                Marcar como lida
              </Button>
            )}

            <Button
              variant="ghost"
              size="xs"
              onClick={() => navigator.clipboard?.writeText(window.location.href).catch(() => {})}
              className="text-muted-foreground hover:text-foreground gap-1.5"
            >
              <Link2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              Copiar link
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled
              className="text-muted-foreground gap-1.5"
              title="Em breve"
            >
              <BellOff className="h-3.5 w-3.5" strokeWidth={1.75} />
              Adiar
            </Button>
          </div>

          {/* Placeholder de comentários (roadmap) */}
          <div className="mt-8 rounded-md border border-dashed border-border/70 bg-muted/20 p-4 flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[12.5px] font-medium text-foreground">
                  Comentários internos
                </p>
                <span className="inline-flex items-center h-[16px] px-1.5 rounded-sm bg-muted text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Beta
                </span>
              </div>
              <p className="text-[11.5px] text-muted-foreground mt-0.5">
                Em breve você poderá conversar com sua equipe direto na notificação.
              </p>
            </div>
          </div>
        </article>
      </ScrollArea>
    </div>
  );
}
